/**
 * Carousel timer helper.
 *
 * Wraps the IntersectionObserver + auto-advance timer pattern shared by
 * carousel-style blocks (the existing `carousel` block; the new
 * `testimonial-feature` block). Pauses when the container leaves the
 * viewport and resumes when it returns. Optionally drives a progress bar
 * element by toggling the `.animating` class on each cycle.
 *
 * Does NOT touch slide markup or transitions — the caller owns the slide
 * swap via the `onAdvance` callback. This keeps the helper agnostic to
 * whether slides are bg-images, content cards, or anything else.
 *
 * @param {Object}      opts
 * @param {Element}     opts.container        Element to observe for visibility.
 * @param {Function}    opts.onAdvance        Called each tick to advance to the next slide.
 * @param {number}      [opts.duration=6000]  Milliseconds per slide.
 * @param {Element}     [opts.progressTracker] Element that gets `.animating` toggled
 *                                            for the duration of each tick.
 *
 * @returns {{ start: Function, pause: Function, stop: Function }}
 *   `start` / `pause` are reversible (used by the IO observer).
 *   `stop` is permanent (used when the user interacts with controls).
 */
export const createCarouselTimer = ({
    container,
    onAdvance,
    duration = 12000,
    progressTracker = null,
}) => {
    let timeout = null;
    let stopped = false;

    const tick = () => {
        console.log('tick', { stopped });
        if (stopped) return;
        if (progressTracker) {
            // Restart the CSS animation by removing+re-adding the class. The
            // forced reflow in between is what makes the browser re-evaluate.
            progressTracker.classList.remove('animating');
            void progressTracker.offsetWidth;
            progressTracker.classList.add('animating');
        }
        timeout = setTimeout(() => {
            if (stopped) return;
            onAdvance();
            tick();
        }, duration);
    };

    const start = () => {
        if (stopped || timeout) return;
        tick();
    };

    const pause = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        if (progressTracker) {
            progressTracker.classList.remove('animating');
        }
    };

    const stop = () => {
        pause();
        stopped = true;
    };

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    start();
                } else {
                    pause();
                }
            });
        }, { threshold: 0.3 });

        observer.observe(container);
    } else {
        // Fallback for ancient browsers: just start it.
        start();
    }

    return { start, pause, stop };
};
